import { Inject, Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { APP_VERSION, DEBUG, PRODUCT } from '../injection-token';
import { Integration, KendoDialogSettings, MaximizerModule } from '../models';
import { Product } from '../models/product.model';
import { Subscription } from '../models/subscription.model';

declare const DEV_TOKEN: string;
declare const DEV_WEBSITE: string;
declare const DEV_API: string;
declare const DEV_FERRET: string;

@Injectable()
export class ContextService {
  website = DEV_WEBSITE;
  api = DEV_API;
  identityToken = '';
  version?: string;
  embedded = false;
  debug = false;
  token = DEV_TOKEN;
  resources: { [Key: string]: string } = {};
  isCloud = false;
  featureClientId?: string;
  smartFill?: string;
  hasCustomerHealthServiceUser = false;
  refresh_token = '';
  alias = '';
  segments = '';
  integration?: Integration;
  timeFormat?: string;
  dateFormat?: string;
  dataCenter?: string;
  customerNumber?: string;
  tenantId?: string;
  subscription?: Subscription;
  ferretUrl = DEV_FERRET;
  modules: MaximizerModule[] = [];
  shortcutsEnabled = true;
  product = Product.Core;

  userMenu?: {
    adminUrl: string;
    mobileUrl: string;
    userPictureUrl: string;
  };

  get currentModuleId(): string {
    return top?.$page?.ModuleSwitcher?.currentModuleId ?? '';
  }

  /**
   * Appends an Angular element to the CRM shell and wires it like a module:
   * the legacy helper forwards `actionCompleted` to `MaxCustomAction`, which
   * is what lets a self-mounted element emit `ChangeCurrentModule`. Safe to
   * call twice; a no-op outside the shell, where `$page` does not exist.
   */
  mountElement(tag: string, selector: string): void {
    top?.$page?.addTagToSelector?.(tag, selector);
  }

  get isSLE(): boolean {
    return this.segments.includes('sales-leader-edition');
  }

  constructor(
    @Inject(DEBUG) debug: boolean,
    @Inject(APP_VERSION) private readonly appVersion: string,
    @Inject(PRODUCT) product: string,
  ) {
    this.debug = debug;
    this.product = product ? (product as Product) : Product.Core;
    try {
      if (product === Product.Outlook) {
        this.loadOutlook();
        return;
      }
      this.loadCore();
    } catch (error) {
      console.warn('Context error:', error);
    }
  }

  private loadCore() {
    if (top) {
      if (top.maxAppPath) {
        this.embedded = true;
        this.token = '';
        this.website = top.maxAppPath;
      }

      this.loadCoreHeaderData();
      this.loadCoreMaximizerIdentity();

      if (top.$resources) {
        this.resources = top.$resources;
      }
      const apiElement = top.document.getElementById(
        'idWebAPIURL',
      ) as HTMLInputElement;
      if (apiElement?.value) {
        this.api = apiElement.value;
      }
      if (top.modulesData?.modules?.length) {
        this.modules = top.modulesData.modules;
      }
      this.shortcutsEnabled = top.modulesData?.shortcutsEnabled !== 'false';
    }
  }

  startDialogMode(): void {
    if (top?.$page) {
      top.$page.MaxKeyAccess.DisableModuleCommand();
      top.$page.MaxKeyAccess.DisableInsertCommand();
      top.$page.MaxKeyAccess.EnableEditCommand();
    }
  }

  endDialogMode(): void {
    if (top?.$page) {
      top.$page.MaxKeyAccess.EnableModuleCommand();
      top.$page.MaxKeyAccess.EnableInsertCommand();
      top.$page.MaxKeyAccess.DisableEditCommand();
    }
  }

  showComponentDialog(tag: string, args: unknown): void {
    top?.$page?.addTagToOpenDialog(tag, args);
  }

  showLegacyKendoModal(settings: KendoDialogSettings): Observable<unknown> {
    if (this.embedded) {
      return new Observable((observer) => {
        top?.MaxShowKendoModal.open(
          settings,
          (result) => {
            observer.next(result);
            observer.complete();
          },
          () => {
            observer.next(null);
            observer.complete();
          },
        );
      });
    }
    return of(null);
  }

  showLegacyTeamOwnerSelector(): Observable<string[] | null> {
    if (this.embedded && top) {
      const settings: KendoDialogSettings = {
        ID: 'PipelineVisualSelectionDialog',
        url: `${top.maxAppPath}/mvc/PipelineVisualSelection/PipelineVisualSelection/Index`,
        height: top.window.outerHeight * 0.8,
        width: top.window.outerWidth * 0.5,
        draggable: true,
        scrollable: true,
      };

      return this.showLegacyKendoModal(settings).pipe(
        map((result) => {
          if (result && Array.isArray(result)) {
            return result;
          }
          return null;
        }),
      );
    }
    return of(null);
  }

  getEdition(): string {
    return (
      (top?.document.getElementById('idEditionParameter') as HTMLInputElement)
        ?.value ?? ''
    );
  }

  openHelp(id: string): void {
    top?.window.postMessage(
      JSON.stringify({
        type: 'help',
        id,
      }),
      '*',
    );
  }

  loadOutlook(): void {
    this.version = this.appVersion;
    // Default to cloud unless explicitly set to 'false' in storage
    this.isCloud = localStorage.getItem('isCloud') !== 'false';
  }

  loadIntegration(integration: Integration): void {
    this.api = integration.api;
    this.identityToken = integration.identityToken;
    this.integration = integration;
  }

  clearAuthentication(): void {
    this.api = '';
    this.website = '';
    this.alias = '';
    this.segments = '';
    this.token = '';
    this.refresh_token = '';
  }

  setCurrentGridKeys(keys: string[]): void {
    top?.MainGridUpdate?.ReplaceEntries(keys);
  }

  private loadCoreHeaderData(): void {
    if (!top?.headerData) return;
    this.identityToken = top.headerData.leadModuleIdentityToken;
    this.featureClientId = top.headerData.featureClientId;
    this.smartFill = top.headerData.smartFill;
    this.isCloud = top.headerData.IsCrmLive?.toLowerCase() === 'true';
    this.hasCustomerHealthServiceUser =
      top.headerData.hasCustomerHealthServiceUser?.toLowerCase() === 'true';
    this.timeFormat = top.headerData.timeFormat?.replace('tt', 'a');
    this.dateFormat =
      top.headerData.dateTimeFormatType === 'long'
        ? top.headerData.longDateFormatWithoutWeekday
        : top.headerData.yeardateformat;
    this.dataCenter = top.headerData.dataCenterID;
    this.customerNumber = top.headerData.customerNumber;
    if (top.headerData.version) {
      this.version = top.headerData.version;
    }
    this.ferretUrl = top.headerData.ferretUrl?.endsWith('/')
      ? top.headerData.ferretUrl.slice(0, -1)
      : (top.headerData.ferretUrl ?? '');

    this.userMenu = {
      adminUrl: top.headerData.userAdminURL,
      mobileUrl: top.headerData.userMobileLink,
      userPictureUrl: top.headerData.userPhotoUrl,
    };
  }

  private loadCoreMaximizerIdentity(): void {
    if (!top?.maximizerIdentity) return;
    this.subscription = {
      isTrial: top.maximizerIdentity.is_trial ?? false,
      start: top.maximizerIdentity.subscription_start_date ?? '',
      end: top.maximizerIdentity.subscription_end_date ?? '',
      totalLicenses: top.maximizerIdentity.total_licenses ?? 0,
      enabledUsers: top.maximizerIdentity.enabled_users ?? 0,
    };
    this.alias = top.maximizerIdentity.alias ?? '';
    this.segments = top.maximizerIdentity.segments ?? '';

    if (top?.headerData) {
      const usedLicenses = Number(top.headerData.usedLicenses);
      this.subscription.usedLicenses = isNaN(usedLicenses) ? 0 : usedLicenses;
    }
  }
}
